import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../../api/api";
import { Routes } from "../../routes";
import { components } from "../../components";
import { hooks } from "../../hooks";
import iconEdit from '../../assets/icons/edit-white.png';
import iconDelete from '../../assets/icons/trash-white.png';
import { usePermission } from '../../hooks/usePermission';

interface Spot {
  id: string;
  patrol_asset_id: string;
  name: string;
  code: string;
  level: number;
}

interface Asset {
  id: string;
  name: string;
  code: string;
}

export const PatrolDetailPage: React.FC = () => {
  const { patrolId } = useParams<{ patrolId: string }>();
  const navigate = useNavigate();
  const { canIndex, canStore, canUpdate, canDelete } = usePermission('patrol_component_index');

  const iconEdt = iconEdit;
  const iconDel = iconDelete;
  const [opacity, setOpacity] = useState<number>(0);
  
  hooks.useOpacity(setOpacity);
  const [spots, setSpots] = useState<Spot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [isMobile, setIsMobile] = useState(false);

  const [spotToDelete, setSpotToDelete] = useState<Spot | null>(null);
  const [spotToEdit, setSpotToEdit] = useState<Spot | null>(null);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<number>(1);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [popupType, setPopupType] = useState<'edit' | 'delete'>('edit');


  const [showAddSpotModal, setShowAddSpotModal] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);

  useEffect(() => {
    if (showSuccessPopup) {
      const timer = setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessPopup]);

  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const showMessage = (message: string, type: 'success' | 'error' = 'success') => {
    const messageEl = document.createElement('div');
    messageEl.textContent = message;
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      border-radius: 8px;
      color: white;
      font-weight: 500;
      z-index: 10000;
      background-color: ${type === 'success' ? '#16a34a' : '#dc2626'};
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      transform: translateX(100%);
      transition: transform 0.3s ease;
      max-width: 300px;
    `;
    document.body.appendChild(messageEl);

    setTimeout(() => {
      messageEl.style.transform = 'translateX(0)';
    }, 100);

    setTimeout(() => {
      messageEl.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (document.body.contains(messageEl)) {
          document.body.removeChild(messageEl);
        }
      }, 300);
    }, 3000);
  };

  const showSuccessPopupMessage = (message: string, type: 'edit' | 'delete') => {
    setSuccessMessage(message);
    setPopupType(type);
    setShowSuccessPopup(true);


  };

  const fetchSpots = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axiosInstance.get(
        `/api/v1/patrol/component/${patrolId}/index?per_page=50`
      );
      
      let formatted = (res.data.data || []).map((s: any) => ({
        id: s.id,
        patrol_asset_id: s.patrol_asset_id,
        code: s.asset?.code || "–",
        level: Number(s.level),
        name: s.asset?.name || "–",
      }));
      
      formatted.sort((a: Spot, b: Spot) => a.level - b.level);
      
      formatted = formatted.map((spot: Spot, index: number) => ({
        ...spot,
        level: index + 1
      }));
      
      setSpots(formatted);
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || "Gagal memuat spots");
      showMessage('Gagal memuat data spots', 'error');
    } finally {
      setLoading(false);
    }
  };

const fetchAssets = async () => {
  try {
    setAssetsLoading(true);
    
    let allAssets: Asset[] = [];
    let currentPage = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const res = await axiosInstance.get(`/api/v1/patrol/asset/index`, {
        params: { 
          page: currentPage,
          per_page: 100
        }
      });
      
      const pageData = (res.data.data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        code: item.code
      }));
      
      allAssets = [...allAssets, ...pageData];
      hasMorePages = currentPage < (res.data.last_page || 1);
      currentPage++;

      if (currentPage > 50) {
        console.warn('Safety limit reached: Stopped at page 50');
        break;
      }
    }
    
    setAssets(allAssets);
    
    if (allAssets.length === 0) {
      showMessage('Tidak ada spot yang tersedia');
    }
    
  } catch (err: any) {
    console.error(err);
    showMessage('Gagal memuat daftar spot', 'error');
  } finally {
    setAssetsLoading(false);
  }
};

  useEffect(() => {
    if (patrolId) fetchSpots();
  }, [patrolId]);

  const handleAddSpotClick = () => {
    setSelectedAsset(null);
    fetchAssets();
    setShowAddSpotModal(true);
  };

  const handleAssignSpot = async () => {
    if (!selectedAsset || !patrolId) {
      showMessage('Pilih spot yang akan di-assign', 'error');
      return;
    }

    try {
      const selectedAssetData = assets.find(a => a.id === selectedAsset);
      await axiosInstance.post(`/api/v1/patrol/component/${patrolId}/store`, {
        patrol_asset_id: selectedAsset,
        code: selectedAssetData?.code || "",
      });

      const newLevel = spots.length + 1;
      setSuccessMessage(`Berhasil menambahkan spot "${selectedAssetData?.name}" di Level ${newLevel}`);
      setPopupType('edit');
      setShowSuccessPopup(true);
      
      setShowAddSpotModal(false);
      setSelectedAsset(null);
      fetchSpots();

    } catch (err: any) {
      console.error(err);
      showMessage('Gagal assign spot: ' + (err.response?.data?.message || "Unknown error"), 'error');
    }
  };

  const handleDeleteClick = (spot: Spot) => setSpotToDelete(spot);
  const handleDeleteCancel = () => setSpotToDelete(null);
  const handleDeleteConfirm = async () => {
    if (!spotToDelete) return;
    try {
      await axiosInstance.delete(
        `/api/v1/patrol/component/${patrolId}/destroy/${spotToDelete.id}`
      );
      const deletedSpotName = spotToDelete.name;
      setSpotToDelete(null);
      
      showSuccessPopupMessage(`Spot "${deletedSpotName}" berhasil dihapus dari patrol`, 'delete');
      
      fetchSpots();
    } catch (err: any) {
      console.error(err);
      showMessage('Gagal menghapus spot: ' + (err.response?.data?.message || "Unknown error"), 'error');
    }
  };

  const handleEditLevelClick = (spot: Spot) => {
    setSpotToEdit(spot);
    setSelectedLevel(spot.level);
    setShowLevelModal(true);
  };

  const handleLevelSave = async () => {
    if (!spotToEdit) return;
    const oldLevel = spotToEdit.level;
    
    try {
      const updatedSpots = [...spots];
      const currentIndex = updatedSpots.findIndex(spot => spot.id === spotToEdit.id);
      const [movedSpot] = updatedSpots.splice(currentIndex, 1);
      updatedSpots.splice(selectedLevel - 1, 0, movedSpot);
      const reorderedSpots = updatedSpots.map((spot, index) => ({
        ...spot,
        level: index + 1
      }));
      
      await axiosInstance.post(
        `/api/v1/patrol/component/${patrolId}/update/${spotToEdit.id}`,
        {
          patrol_asset_id: spotToEdit.patrol_asset_id,
          code: spotToEdit.code,
          level: selectedLevel,
        }
      );
      
      setShowLevelModal(false);
      setSpotToEdit(null);
      
      setSpots(reorderedSpots);
      
      showSuccessPopupMessage(
        `Level spot "${spotToEdit.name}" berhasil diubah dari Level ${oldLevel} menjadi Level ${selectedLevel}`, 
        'edit'
      );
      
    } catch (err: any) {
      console.error(err);
      showMessage('Gagal update level spot', 'error');
    }
  };

  const mobileBottomPadding = isMobile ? "50px" : "16px";
  
  const styles = `
    .patrol-detail-container {
      height: 100vh;
      background-color: #f8fafc;
      font-family: 'Inter', sans-serif;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      padding-bottom: ${mobileBottomPadding};
      width: 100%;
    }

    /* Header Styles*/
    .detail-header {
      background-color: #ffffff;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      position: sticky;
      top: 0;
      z-index: 10;
      border-bottom: 1px solid #e2e8f0;
      flex-shrink: 0;
      width: 100%;
    }

    .header-content {
      padding: 16px;
      width: 100%;
      box-sizing: border-box;
    }

    .back-button {
      background: none;
      border: none;
      padding: 8px;
      border-radius: 50%;
      cursor: pointer;
      color: #0f172a;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s;
      margin-bottom: 16px;
    }

    .back-button:hover {
      background-color: #f1f5f9;
    }

    .back-icon {
      font-size: 24px;
      font-weight: bold;
    }

    .header-info {
      display: flex;
      flex-direction: column;
    }

    .header-title {
      font-size: 20px;
      font-weight: 700;
      color: #0f172a;
      margin: 0 0 4px 0;
    }

    .header-subtitle {
      font-size: 14px;
      color: #64748b;
      margin: 0;
    }

    /* Main Content*/
    .main-content {
      padding: 16px;
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      width: 100%;
      box-sizing: border-box;
    }

    /* Section Header */
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      flex-shrink: 0;
      width: 100%;
    }

    .section-title {
      font-size: 24px;
      font-weight: 700;
      color: #0f172a;
      margin: 0;
    }

    .add-button {
      background-color: #2563eb;
      color: white;
      font-weight: 600;
      padding: 10px 20px;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      transition: background-color 0.3s;
      display: flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3);
    }

    .add-button:hover {
      background-color: #1d4ed8;
    }

    .add-icon {
      font-size: 16px;
      font-weight: bold;
    }

    /* Content Section*/
    .content-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      width: 100%;
    }

    /* Scroll Container*/
    .scroll-container {
      flex: 1;
      overflow-y: auto;
      padding-right: 4px;
      width: 100%;
    }

    /*Cards Container*/
    .cards-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding-bottom: 8px;
      width: 100%;
    }

    /* Spot Card*/
    .spot-card {
      background: #ffffff;
      border-radius: 12px;
      border: 1px solid #e2e8f0;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      padding: 16px;
      cursor: pointer;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      flex-shrink: 0;
      width: 100%;
      box-sizing: border-box;
    }

    .spot-card:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }

    .card-content {
      margin-bottom: 0;
      width: 100%;
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 12px;
      width: 100%;
    }

    .card-info {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
      min-width: 0;
      width: 100%;
    }


    .spot-name {
      font-size: 16px;
      font-weight: 600;
      color: #0f172a;
      margin: 0;
      line-height: 1.4;
      word-break: break-word;
      white-space: normal;
      overflow: visible;
      text-overflow: unset;
      display: block;
      width: 100%;
    }

    .spot-details {
      display: flex;
      flex-direction: column;
      gap: 6px;
      width: 100%;
    }

    .detail-row {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      width: 100%;
    }

    .detail-label {
      color: #64748b;
      font-weight: 500;
      min-width: 40px;
    }

    .level-badge {
      background-color: #2563eb;
      color: white;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .code-badge {
      background-color: #f1f5f9;
      color: #475569;
      padding: 4px 8px;
      border-radius: 6px;
      font-size: 12px;
      font-family: monospace;
      font-weight: 500;
    }


    .card-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 12px;
      padding-top: 16px;
      border-top: 1px solid #e2e8f0;
      width: 100%;
    }

    .btn-edit {
      background: #dbeafe;
      color: #2563eb;
      font-weight: 600;
      padding: 8px 16px;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      transition: background-color 0.3s;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .btn-edit:hover {
      background: #bfdbfe;
    }

    .btn-delete {
      background: #fee2e2;
      color: #dc2626;
      font-weight: 600;
      padding: 8px 16px;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      transition: background-color 0.3s;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .btn-delete:hover {
      background: #fecaca;
    }

    /* Loading State */
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 80px 20px;
      color: #64748b;
      flex: 1;
      width: 100%;
    }

    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #2563eb;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 16px;
    }


    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: white;
      border-radius: 12px;
      border: 1px solid #e2e8f0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 1;
      width: 100%;
    }

    .empty-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .empty-state h3 {
      color: #374151;
      margin: 0 0 8px 0;
      font-size: 20px;
      font-weight: 600;
    }

    .empty-state p {
      margin: 0 0 20px 0;
      color: #6b7280;
      font-size: 16px;
    }


    .error-state {
      text-align: center;
      padding: 60px 20px;
      background: white;
      border-radius: 12px;
      border: 1px solid #fecaca;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 1;
      width: 100%;
    }

    .error-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .error-state h3 {
      color: #dc2626;
      margin: 0 0 8px 0;
      font-size: 20px;
      font-weight: 600;
    }

    /* Modal Styles */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
    }

    .modal {
      background: white;
      border-radius: 16px;
      padding: 0;
      max-width: 500px;
      width: 100%;
      max-height: 90vh;
      overflow: hidden;
      box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
      animation: modalSlideIn 0.3s ease-out;
    }

    .modal-header {
      padding: 24px 24px 0 24px;
      margin-bottom: 20px;
    }

    .modal-title {
      font-size: 18px;
      font-weight: 600;
      color: #0f172a;
      margin: 0 0 8px 0;
    }

    .modal-message {
      font-size: 14px;
      color: #475569;
      margin: 0;
      line-height: 1.5;
    }

    .modal-body {
      padding: 0 24px;
      max-height: 60vh;
      overflow-y: auto;
    }

    .form-group {
      margin-bottom: 16px;
    }

    .form-label {
      display: block;
      font-size: 14px;
      font-weight: 600;
      color: #374151;
      margin-bottom: 8px;
    }

    .select-field {
      width: 100%;
      padding: 12px 16px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      font-size: 14px;
      background: white;
      transition: border-color 0.2s;
    }

    .select-field:focus {
      outline: none;
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
    }

    /* Assets List untuk modal tambah spot */
    .assets-list {
      max-height: 300px;
      overflow-y: auto;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
    }

    .asset-item {
      padding: 12px 16px;
      border-bottom: 1px solid #f1f5f9;
      cursor: pointer;
      transition: background-color 0.2s;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .asset-item:hover {
      background-color: #f8fafc;
    }

    .asset-item.selected {
      background-color: #dbeafe;
      border-left: 3px solid #2563eb;
    }

    .asset-radio {
      width: 16px;
      height: 16px;
      accent-color: #2563eb;
    }

    .asset-info {
      flex: 1;
    }

    .asset-name {
      font-size: 14px;
      font-weight: 600;
      color: #0f172a;
      margin: 0 0 4px 0;
    }

    .asset-code {
      font-size: 12px;
      color: #64748b;
      font-family: monospace;
      margin: 0;
    }

    .selected-badge {
      background-color: #16a34a;
      color: white;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
    }

    .modal-actions {
      padding: 24px;
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      border-top: 1px solid #e2e8f0;
    }

    .modal-cancel-button {
      padding: 12px 20px;
      background: #6b7280;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: background-color 0.2s;
    }

    .modal-cancel-button:hover {
      background: #4b5563;
    }

    .modal-delete-button {
      padding: 12px 20px;
      background: #dc2626;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: background-color 0.2s;
    }

    .modal-delete-button:hover {
      background: #b91c1c;
    }

    .modal-save-button {
      padding: 12px 20px;
      background: #2563eb;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: background-color 0.2s;
    }

    .modal-save-button:hover {
      background: #1d4ed8;
    }

    .modal-assign-button {
      padding: 12px 20px;
      background: #16a34a;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: background-color 0.2s;
    }

    .modal-assign-button:hover {
      background: #15803d;
    }

    .modal-assign-button:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }

    /* Success Popup */
    .success-popup {
      background: white;
      border-radius: 16px;
      padding: 24px;
      max-width: 400px;
      width: 100%;
      box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
      text-align: center;
      position: relative;
    }

    .success-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .popup-title {
      font-size: 18px;
      font-weight: 600;
      color: #0f172a;
      margin: 0 0 12px 0;
    }

    .popup-message {
      font-size: 14px;
      color: #475569;
      line-height: 1.5;
      margin: 0 0 16px 0;
    }


    .progress-bar {
      height: 4px;
      background-color: #e5e7eb;
      border-radius: 2px;
      overflow: hidden;
      margin-top: 16px;
    }

    .progress-fill {
      height: 100%;
      border-radius: 2px;
      animation: shrink 3s linear forwards;
    }

    .popup-close {
      position: absolute;
      top: 16px;
      right: 16px;
      background: none;
      border: none;
      font-size: 18px;
      cursor: pointer;
      color: #64748b;
      padding: 4px;
      border-radius: 4px;
      transition: background-color 0.2s;
    }

    .popup-close:hover {
      background-color: #f1f5f9;
    }

    /* Animations */
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @keyframes modalSlideIn {
      from { opacity: 0; transform: translateY(-10px) scale(.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    @keyframes shrink {
      0% { width: 100%; }
      100% { width: 0%; }
    }


    @media (max-width: 768px) {
      .main-content {
        padding: 12px;
      }

      .section-header {
        flex-direction: column;
        gap: 16px;
        align-items: flex-start;
      }

      .card-actions {
        justify-content: flex-end;
      }

      .modal-actions {
        flex-direction: column;
      }

      .modal {
        max-width: 90%;
      }
    }
  `;

  if (loading) return (
    <div className="patrol-detail-container">
      <style>{styles}</style>
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Memuat data spots...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="patrol-detail-container">
      <style>{styles}</style>
      <div className="error-state">

        <h3>Terjadi Kesalahan</h3>
        <p>{error}</p>
      </div>
    </div>
  );

  const maxLevel = spots.length;

  return (
    <div className="patrol-detail-container">
      <style>{styles}</style>

      {/* Header */}
      <header className="detail-header">
        <div className="header-content">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <button 
              onClick={() => navigate(Routes.PatrolFormTable)} 
              className="back-button"
            >
              <span className="back-icon">←</span>
            </button>
            <div className="header-info">
              <h1 className="header-title">Detail Spot Patrol</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Header with Add Button */}
        <div className="section-header">
          <h2 className="section-title">Kelola Spot</h2>
          {canStore && (
            <button
            onClick={handleAddSpotClick}
            className="add-button"
          >
            <span className="add-icon">+</span>
            <span>Tambah Spot</span>
          </button>
          )}
        </div>

        {canIndex && (
          <div className="content-section">
            <div className="scroll-container">
              <div className="cards-container">
                {spots.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📍</div>
                    <h3>Belum Ada Spot</h3>
                    <p>Mulai dengan menambahkan spot pertama untuk patrol ini</p>
                    {canStore && (
                      <button onClick={handleAddSpotClick} className="add-button">
                        <span className="add-icon">+</span>
                        <span>Tambah Spot Pertama</span>
                      </button>
                    )}
                  </div>
                ) : (
                  spots.map((spot) => (
                    <div key={spot.id} className="spot-card">
                      <div className="card-content">
                        <div className="card-header">
                          <div className="card-info">
                            <h4 className="spot-name">{spot.name}</h4>
                            <div className="spot-details">
                              <div className="detail-row">
                                <span className="detail-label">Kode:</span>
                                <code className="code-badge">{spot.code}</code>
                              </div>
                              <div className="detail-row">
                                <span className="detail-label">Level:</span>
                                <span className="level-badge">
                                  <span>#</span>
                                  {spot.level}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card-actions">
                        {canUpdate && (
                          <button
                            className="btn-edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditLevelClick(spot);
                            }}
                          >
                            Ubah
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(spot);
                            }}
                          >
                            Hapus
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {spotToDelete && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Konfirmasi Hapus</h3>
              <p className="modal-message">
                Apakah Anda yakin ingin menghapus spot <strong>"{spotToDelete.name}"</strong>? 
                Tindakan ini tidak dapat dibatalkan.
              </p>
            </div>
            <div className="modal-body">
              <div className="modal-actions">
                <button 
                  onClick={handleDeleteCancel} 
                  className="modal-cancel-button"
                >
                  Batal
                </button>
                <button 
                  onClick={handleDeleteConfirm} 
                  className="modal-delete-button"
                >
                  Ya, Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Level Modal */}
      {showLevelModal && spotToEdit && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Edit Level Spot</h3>
              <p className="modal-message">
                Ubah urutan level untuk spot: <strong>"{spotToEdit.name}"</strong>
              </p>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Pilih Level Baru</label>
                <select
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(Number(e.target.value))}
                  className="select-field"
                >
                  {Array.from({ length: spots.length }, (_, i) => i + 1).map((lvl) => (
                    <option key={lvl} value={lvl}>
                      Level {lvl} {lvl === 1 ? '(Pertama)' : lvl === spots.length ? '(Terakhir)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button 
                  onClick={() => setShowLevelModal(false)} 
                  className="modal-cancel-button"
                >
                  Batal
                </button>
                <button 
                  onClick={handleLevelSave} 
                  className="modal-save-button"
                >
                  Simpan Level
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Spot Modal */}
      {showAddSpotModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Tambah Spot Patrol</h3>
              <p className="modal-message">
                Pilih spot yang ingin ditambahkan ke jadwal patrol
              </p>
            </div>
            <div className="modal-body">
              {assetsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  Memuat daftar spot...
                </div>
              ) : (
                <>
                  {selectedAsset && (
                    <div style={{
                      textAlign: 'center',
                      fontSize: '14px',
                      color: '#16a34a',
                      fontWeight: '500',
                      padding: '8px 12px',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '8px',
                      border: '1px solid #d1fae5',
                      marginBottom: '16px'
                    }}>
                      1 spot dipilih
                    </div>
                  )}
                  
                  <div className="assets-list">
                    {assets.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        <div style={{ fontSize: '36px', marginBottom: '12px' }}>📭</div>
                        Tidak ada spot yang tersedia
                      </div>
                    ) : (
                      assets.map((asset) => (
                        <div
                          key={asset.id}
                          className={`asset-item ${selectedAsset === asset.id ? 'selected' : ''}`}
                          onClick={() => setSelectedAsset(asset.id)}
                        >
                          <input
                            type="radio"
                            name="spot"
                            value={asset.id}
                            checked={selectedAsset === asset.id}
                            onChange={() => setSelectedAsset(asset.id)}
                            className="asset-radio"
                          />
                          <div className="asset-info">
                            <div className="asset-name">{asset.name}</div>
                            <div className="asset-code">{asset.code}</div>
                          </div>
                          {selectedAsset === asset.id && (
                            <div className="selected-badge">Dipilih</div>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {!selectedAsset && (
                    <div style={{
                      textAlign: 'center',
                      fontSize: '13px',
                      color: '#6b7280',
                      fontStyle: 'italic',
                      padding: '16px'
                    }}>
                      Pilih salah satu spot dari daftar di atas untuk melanjutkan
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-actions">
              <button 
                onClick={() => setShowAddSpotModal(false)} 
                className="modal-cancel-button"
              >
                Batal
              </button>
              <button 
                onClick={handleAssignSpot}
                disabled={!selectedAsset || assetsLoading}
                className="modal-assign-button"
              >
                Assign Spot
              </button>
            </div>
          </div>
        </div>
      )}


      {showSuccessPopup && (
        <div 
          className="modal-overlay"
          onClick={() => setShowSuccessPopup(false)}
        >
          <div 
            className="success-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="popup-close"
              onClick={() => setShowSuccessPopup(false)}
            >
              ✕
            </button>
            <div 
              className="success-icon"
              style={{ 
                color: popupType === 'edit' ? '#16a34a' : '#2563eb'
              }}
            >
              {popupType === 'edit'}
            </div>
            <h3 className="popup-title">
              {popupType === 'edit' ? 'Berhasil!' : 'Spot Dihapus!'}
            </h3>
            <p className="popup-message">{successMessage}</p>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{
                  backgroundColor: popupType === 'edit' ? '#16a34a' : '#2563eb'
                }}
              ></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

